import { Button, Spinner, Text } from '@/ui-kit'
import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod/v3'
import { FormSelect } from '../missions/components/FormSelect'
import { FormSelectDropdown } from '../missions/components/FormSelectDropdown'
import { toSelectOptions, useCountries, useLanguages } from './api/dictionaries'

const AGE_BRACKET_OPTIONS: { value: string; label: string }[] = [
  { value: '18-', label: 'Under 18' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '45-54', label: '45–54' },
  { value: '55-64', label: '55–64' },
  { value: '65+', label: '65+' }
]

const GENDER_OPTIONS: { value: string; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
]

const RACE_ETHNICITY_OPTIONS: { value: string; label: string }[] = [
  { value: 'white', label: 'White' },
  { value: 'asian', label: 'Asian' },
  { value: 'middle-eastern', label: 'Middle Eastern' },
  { value: 'hispanic-latino', label: 'Hispanic or Latin' },
  { value: 'black-african', label: 'Black or African Descent' },
  { value: 'multiracial', label: 'Multiracial' },
  { value: 'other', label: 'Other' }
]

const LANGUAGE_LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: 'A1', label: 'A1 - Beginner' },
  { value: 'A2', label: 'A2 - Elementary' },
  { value: 'B1', label: 'B1 - Intermediate' },
  { value: 'B2', label: 'B2 - Upper Intermediate' },
  { value: 'C1', label: 'C1 - Advanced' },
  { value: 'C2', label: 'C2 - Proficient' },
  { value: 'Native', label: 'Native / Bilingual' }
]

type LangOption = { value: string; label: string }

/** Native language: hide codes already chosen as a secondary language (current selection stays visible). */
function filterNativeLanguageOptions(
  options: readonly LangOption[],
  nativeLanguage: string,
  secondaryLanguages: { language: string; level: string }[]
): LangOption[] {
  const usedInSecondary = new Set(
    secondaryLanguages.map((s) => s.language).filter((code): code is string => Boolean(code))
  )
  return options.filter((opt) => !usedInSecondary.has(opt.value) || opt.value === nativeLanguage)
}

/** Secondary row: hide native language and languages picked on other rows (this row’s value stays visible). */
function filterSecondaryLanguageOptions(
  options: readonly LangOption[],
  nativeLanguage: string,
  secondaryLanguages: { language: string; level: string }[],
  rowIndex: number
): LangOption[] {
  const taken = new Set<string>()
  if (nativeLanguage) taken.add(nativeLanguage)
  secondaryLanguages.forEach((s, i) => {
    if (i !== rowIndex && s.language) taken.add(s.language)
  })
  const current = secondaryLanguages[rowIndex]?.language
  return options.filter((opt) => !taken.has(opt.value) || opt.value === current)
}

const isAllowedLanguageCode = (code: string, allowedCodes: Set<string>) => {
  if (!code) return false
  if (!allowedCodes.size) return true
  return allowedCodes.has(code)
}

function createProfileSchema(languageCodes: readonly string[]) {
  const allowedLanguageCodes = new Set(languageCodes)

  return z.object({
    ageBracket: z.string().min(1, 'Select age bracket'),
    gender: z.string().min(1, 'Select gender'),
    raceEthnicity: z.string().min(1, 'Select race/ethnicity'),
    countryOfOriginCode: z.string().min(1, 'Select country of origin'),
    countryOfResidenceCode: z.string().min(1, 'Select country of residence'),
    nativeLanguage: z
      .string()
      .min(1, 'Select native language')
      .refine((code) => isAllowedLanguageCode(code, allowedLanguageCodes), 'Select language from the list'),
    dialectCountry: z.string().optional(),
    secondaryLanguages: z
      .array(
        z.object({
          language: z
            .string()
            .min(1, 'Select language')
            .refine((code) => isAllowedLanguageCode(code, allowedLanguageCodes), 'Select language from the list'),
          level: z.string().min(1, 'Select language level')
        })
      )
      .max(5, 'Maximum 5 secondary languages')
  })
}

export type ProfileDetailsFormValues = z.infer<ReturnType<typeof createProfileSchema>>

type ProfileDetailsFormProps = {
  onSubmit: (data: ProfileDetailsFormValues) => void
  isSubmittingProfile: boolean
  defaultValues?: Partial<ProfileDetailsFormValues>
  className?: string
  submitButtonText?: string
}

export const ProfileDetailsForm = ({
  onSubmit,
  isSubmittingProfile,
  defaultValues,
  className,
  submitButtonText = 'Submit'
}: ProfileDetailsFormProps) => {
  const { options: countryOptions } = useCountries()
  const { options: languageOptions } = useLanguages()
  const availableLanguages = useMemo(
    () => toSelectOptions(languageOptions.filter((language) => language.iso1 !== null)),
    [languageOptions]
  )

  const languageCodes = useMemo(() => availableLanguages.map((language) => language.value), [availableLanguages])
  const missionDemographicsSchema = useMemo(() => createProfileSchema(languageCodes), [languageCodes])

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<ProfileDetailsFormValues>({
    resolver: zodResolver(missionDemographicsSchema),
    defaultValues: {
      ageBracket: '',
      gender: '',
      raceEthnicity: '',
      countryOfOriginCode: '',
      countryOfResidenceCode: '',
      nativeLanguage: '',
      secondaryLanguages: [],
      ...defaultValues
    }
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'secondaryLanguages' })

  const nativeLanguage = watch('nativeLanguage')
  const secondaryLanguages = watch('secondaryLanguages') ?? []

  const nativeLanguageOptions = filterNativeLanguageOptions(availableLanguages, nativeLanguage, secondaryLanguages)

  const updateField = (name: keyof ProfileDetailsFormValues, value: string) => {
    setValue(name, value, { shouldValidate: true })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={clsx('flex flex-col gap-6', className)}>
      <div className="flex flex-col gap-6">
        <Text type="subtitle-semibold" className="text-gray-70">
          Demographics
        </Text>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelect
            value={watch('ageBracket')}
            onChange={(v) => updateField('ageBracket', v)}
            label="Age bracket"
            options={AGE_BRACKET_OPTIONS}
            placeholder="Select age bracket"
            errorMessage={errors.ageBracket?.message}
          />
          <FormSelect
            value={watch('gender')}
            onChange={(v) => updateField('gender', v)}
            label="Gender"
            options={GENDER_OPTIONS}
            placeholder="Select gender"
            errorMessage={errors.gender?.message}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelect
            value={watch('raceEthnicity')}
            onChange={(v) => updateField('raceEthnicity', v)}
            label="Race/ethnicity"
            options={RACE_ETHNICITY_OPTIONS}
            placeholder="Select race/ethnicity"
            errorMessage={errors.raceEthnicity?.message}
          />
          <FormSelectDropdown
            value={watch('countryOfResidenceCode')}
            onChange={(v) => updateField('countryOfResidenceCode', v)}
            label="Country of residence"
            options={countryOptions}
            placeholder="Select country"
            errorMessage={errors.countryOfResidenceCode?.message}
            searchable
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 border-t border-solid border-gray-100 pt-6">
        <div className="mb-2 flex flex-col gap-2">
          <Text type="subtitle-semibold" className="text-gray-70">
            Languages
          </Text>
          <div className="text-body-2 text-gray-90 flex flex-col gap-4">
            Your primary language is the one you use most often and feel most comfortable with for reading and
            communication. Your secondary language is an additional language you understand or use occasionally, but not
            as your main preference.
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelectDropdown
            value={watch('nativeLanguage')}
            valueLabel={languageOptions.find((language) => language.code === nativeLanguage)?.name}
            onChange={(v) => updateField('nativeLanguage', v)}
            label="Native language"
            options={nativeLanguageOptions}
            placeholder="Select language"
            errorMessage={errors.nativeLanguage?.message}
            searchable
          />

          <FormSelectDropdown
            value={watch('countryOfOriginCode')}
            onChange={(v) => updateField('countryOfOriginCode', v)}
            label="Country(Dialect)"
            options={countryOptions}
            placeholder="Select country"
            errorMessage={errors.countryOfOriginCode?.message}
            searchable
          />
        </div>
        <div className="mt-8 flex flex-col gap-6">
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-wrap items-start justify-center gap-4">
              <div className="max-mobile:max-w-full max-w-1/2 min-w-[300px] flex-1">
                <FormSelectDropdown
                  value={watch(`secondaryLanguages.${index}.language`)}
                  valueLabel={
                    languageOptions.find((language) => language.code === secondaryLanguages[index].language)?.name
                  }
                  onChange={(v) => setValue(`secondaryLanguages.${index}.language`, v, { shouldValidate: true })}
                  label="Language"
                  options={filterSecondaryLanguageOptions(
                    availableLanguages,
                    nativeLanguage,
                    secondaryLanguages,
                    index
                  )}
                  placeholder="Select Language"
                  errorMessage={errors.secondaryLanguages?.[index]?.language?.message}
                  searchable
                />
              </div>
              <div className="flex min-w-[300px] flex-1 gap-4">
                <FormSelect
                  className="flex-1"
                  value={watch(`secondaryLanguages.${index}.level`)}
                  onChange={(v) =>
                    setValue(`secondaryLanguages.${index}.level`, v, {
                      shouldValidate: true
                    })
                  }
                  label="Language Level"
                  options={LANGUAGE_LEVEL_OPTIONS}
                  placeholder="Select Language Level"
                  errorMessage={errors.secondaryLanguages?.[index]?.level?.message}
                />
                <Button
                  type="button"
                  variant="bordered-gray"
                  onClick={() => remove(index)}
                  aria-label="Remove language"
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
          ))}
          {fields.length < 5 && (
            <Button
              type="button"
              variant="bordered-gray"
              size="small"
              onClick={() =>
                append({
                  language: '',
                  level: 'A1'
                })
              }
              className="max-mobile:w-full self-start"
            >
              <Plus size={16} className="mr-1.5 shrink-0" />
              Add secondary language
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 border-t border-solid border-gray-100 pt-6">
        <Button
          type="submit"
          className="max-mobile:w-full max-mobile:max-w-full max-w-[300px]"
          isDisabled={isSubmittingProfile}
        >
          {isSubmittingProfile && <Spinner size="small" />}
          {submitButtonText}
        </Button>
      </div>
    </form>
  )
}
